<script setup lang="ts">
import { useWindowScroll } from '@vueuse/core'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useAuthStore } from '~/stores/auth'

const authStore = useAuthStore()
const { y: scroll } = useWindowScroll()
const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})

const navigation = ref([
  { id: 'home', title: 'Home', isActive: false },
  { id: 'feature', title: 'Fitur Utama', isActive: false },
  { id: 'solusi', title: 'Solusi', isActive: false },
  { id: 'partner', title: 'Partner', isActive: false },
  { id: 'testimoni', title: 'Testimoni', isActive: false },
  { id: 'faq', title: 'FAQ', isActive: false },
])

const collapse = ref(false)

function openMenu(val: boolean) {
  collapse.value = val
}

function updateActiveSection() {
  navigation.value.forEach((section) => {
    const element = document.getElementById(section.id)
    if (!element)
      return
    const top = element.offsetTop - 200
    const bottom = top + element.offsetHeight
    const scrollTop = window.pageYOffset
    section.isActive = scrollTop >= top && scrollTop < bottom
  })
}

function scrollToSection(sectionId: string) {
  const element = document.getElementById(sectionId)
  if (!element)
    return
  const headerOffset = 120
  const elementPosition = element.getBoundingClientRect().top
  const offsetPosition = elementPosition + window.pageYOffset - headerOffset
  window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
  openMenu(false)
}

onMounted(() => {
  updateActiveSection()
  window.addEventListener('scroll', updateActiveSection)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateActiveSection)
})
</script>

<template>
  <div
    class="fixed top-0 w-full z-40 bg-white border-b border-neutral-200 transition-shadow duration-300 ease-in-out"
    :class="scroll > 50 ? 'shadow-sm' : 'shadow-none'"
  >
    <nav class="container relative z-20 grid grid-cols-[1fr_auto_1fr] items-center py-5 mx-auto xs:flex xs:justify-between xs:items-center xs:px-4">
      <!-- Logo -->
      <img
        class="object-contain w-8 h-8 ml-[40px] xs:ml-0 cursor-pointer"
        src="/img/logo-assets/logo-rintisan-not-text.png"
        alt="Rintisan"
      >

      <!-- Desktop nav links (center col) -->
      <div class="xs:hidden">
        <ul class="flex items-center gap-4">
          <div
            v-for="section in navigation"
            :key="section.id"
            class="px-4 py-1 transition-all duration-500 ease-in-out"
            :class="section.isActive ? 'flex justify-center rounded-full bg-[#F3F4F6]' : ''"
          >
            <a
              class="text-base transition-all duration-500 ease-in-out font-geist"
              :class="section.isActive ? 'text-neutral-900 font-bold' : 'text-neutral-900'"
              :href="`#${section.id}`"
              @click.prevent="scrollToSection(section.id)"
            >{{ section.title }}</a>
          </div>
        </ul>
      </div>

      <!-- Right side: auth buttons (desktop) + hamburger (mobile) -->
      <div class="flex items-center justify-end">
        <!-- Mobile hamburger -->
        <button class="sm:hidden" @click.stop="openMenu(!collapse)">
          <svg v-if="!collapse" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12h18M3 6h18M3 18h18" stroke="#5D5C62" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <svg v-else width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="#5D5C62" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>

        <!-- Desktop: skeleton while hydrating -->
        <div v-if="!isMounted" class="xs:hidden flex items-center gap-3 mr-5">
          <div class="w-16 h-5 rounded bg-neutral-200 animate-pulse" />
          <div class="w-28 h-9 rounded-md bg-neutral-200 animate-pulse" />
        </div>
        <!-- Desktop: guest buttons -->
        <div v-else-if="!authStore.token" class="xs:hidden flex items-center mr-5">
          <button class="w-24 text-base font-normal text-neutral-800 font-geist" @click="navigateTo('/masuk')">
            Masuk
          </button>
          <button class="px-4 py-[10px] text-base text-white rounded-md bgGradientV2 font-geist" @click="navigateTo('/daftar')">
            Coba Sekarang
          </button>
        </div>
        <!-- Desktop: dashboard button -->
        <button v-else class="xs:hidden w-24 text-base font-normal text-neutral-800 font-geist mr-5" @click="navigateTo('/dashboard')">
          Dashboard
        </button>
      </div>

      <!-- Mobile menu dropdown -->
      <div
        v-if="collapse"
        class="absolute top-[70px] animate-fadeIn flex flex-col justify-between shadow-lg left-0 z-40 w-full h-screen pt-5 bg-white sm:hidden rounded-b-xl pb-7"
      >
        <div class="grid grid-cols-1 px-5">
          <div v-for="section in navigation" :key="section.id" class="w-full">
            <div
              :class="section.isActive ? 'bg-[#F3F4F6]' : ''"
              class="w-full px-4 py-[10px] rounded-md"
              @click.prevent="scrollToSection(section.id)"
            >
              <a
                :href="`#${section.id}`"
                class="w-full block text-neutral-900 font-geist"
                :class="section.isActive ? 'font-bold text-base' : 'text-base font-normal'"
              >{{ section.title }}</a>
            </div>
          </div>
        </div>
        <div class="mb-[60px]">
          <div v-if="!isMounted" class="px-4 mt-3 space-y-4">
            <div class="w-full h-10 rounded-md bg-neutral-200 animate-pulse" />
            <div class="w-full h-10 rounded-md bg-neutral-200 animate-pulse" />
          </div>
          <div v-else-if="!authStore.token" class="px-4 mt-3">
            <button class="w-full text-base font-normal text-left px-4 py-[10px] rounded-md bg-[#F3F4F6] text-neutral-800 font-geist" @click="navigateTo('/masuk')">
              Masuk
            </button>
            <button class="px-4 py-[10px] w-full text-base mt-4 rounded-md text-white bgGradientV2 font-geist" @click="navigateTo('/daftar')">
              Coba Sekarang
            </button>
          </div>
          <div v-else class="px-4 mt-5">
            <button class="w-full text-base font-normal text-left px-4 py-[10px] rounded-md bg-[#F3F4F6] text-neutral-800 font-geist" @click="navigateTo('/dashboard')">
              Dashboard
            </button>
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>
